import React, { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useApolloClient } from '@apollo/react-hooks'
import { LoaderCircle } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Label } from '@/components/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Table, TableBody, TableCell, TableRow } from '@/components/ui/table'
import * as Query from '../../../api/gql/query'
import { compareMoneyStrings } from '../lib/contracts'
import { describeCostControlError } from '../lib/errors'
import { formatMoney } from '../lib/money'
import type { CostTree } from '../lib/tree'
import type { CostAllocationLine } from '../types'
import MoneyInput from './MoneyInput'

const DEPTH_PAD: Record<number, string> = { 0: 'pl-2', 1: 'pl-6', 2: 'pl-10', 3: 'pl-14' }

interface Props {
  open: boolean
  projectId: string
  tree: CostTree
  currency: string
  locale: string
  initialAmount: string | null
  onOpenChange: (open: boolean) => void
  onApply: (lines: { nodeId: string; amount: string }[]) => void
  /** i18n key of the apply button; contracts add to the allocation, changes to their lines. */
  applyKey?: string
}

/** Spec §4: an amount split over a chosen group's items pro rata to their revised budget. The server computes the preview. */
const AllocateDialog: React.FC<Props> = ({ open, projectId, tree, currency, locale, initialAmount, onOpenChange, onApply, applyKey = 'CostControl_allocate_apply' }) => {
  const { t } = useTranslation()
  const client = useApolloClient()
  const groups = useMemo(() => tree.order.filter((n) => n.activity.kind === 'GROUP'), [tree])
  const [amount, setAmount] = useState<string | null>(null)
  const [amountValid, setAmountValid] = useState(true)
  const [rootId, setRootId] = useState('')
  const [preview, setPreview] = useState<CostAllocationLine[] | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    if (!open) return
    setAmount(initialAmount)
    setAmountValid(true)
    setRootId(groups[0]?.activity.id ?? '')
    setPreview(null)
    setError(null)
    setLoading(false)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])
  // Any change of the inputs makes the preview stale. They are locked while a preview loads, so a late answer
  // always belongs to the amount and group on screen.
  useEffect(() => { setPreview(null); setError(null) }, [amount, rootId])

  const money = (v: string) => formatMoney(v, { locale, currency })
  const canPreview = !!rootId && amountValid && amount !== null && compareMoneyStrings(amount, '0') > 0
  const runPreview = async () => {
    if (!canPreview || loading) return
    setLoading(true)
    setError(null)
    try {
      const { data } = await client.query<{ costAllocationPreview: CostAllocationLine[] }>({
        query: Query.COST_ALLOCATION_PREVIEW,
        variables: { projectId, amount, rootNodeId: rootId },
        fetchPolicy: 'network-only',
        context: { handlesErrors: true },
      })
      setPreview(data.costAllocationPreview)
    } catch (e: any) {
      setError(describeCostControlError(e?.message, t))
    } finally {
      setLoading(false)
    }
  }
  const apply = () => {
    if (!preview) return
    onApply(preview.map((l) => ({ nodeId: l.nodeId, amount: l.amount })))
    onOpenChange(false)
  }

  return (
    <Dialog open={open} onOpenChange={(next) => { if (!loading) onOpenChange(next) }}>
      <DialogContent className="sm:max-w-xl">
        <DialogHeader>
          <DialogTitle>{t('CostControl_allocate_title')}</DialogTitle>
          <DialogDescription>{t('CostControl_allocate_hint')}</DialogDescription>
        </DialogHeader>
        <div className="grid gap-4 sm:grid-cols-2">
          <div className="space-y-1.5">
            <Label htmlFor="cc-alloc-amount">{`${t('CostControl_allocate_amount')} (${currency})`}</Label>
            <MoneyInput id="cc-alloc-amount" value={amount} disabled={loading} onChange={(v, valid) => { setAmount(v); setAmountValid(valid) }} />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="cc-alloc-root">{t('CostControl_allocate_root')}</Label>
            <Select value={rootId} onValueChange={setRootId} disabled={loading}>
              <SelectTrigger id="cc-alloc-root"><SelectValue placeholder={t('CostControl_allocate_root_placeholder')} /></SelectTrigger>
              <SelectContent>
                {groups.map((g) => (
                  <SelectItem key={g.activity.id} value={g.activity.id} className={DEPTH_PAD[g.depth] ?? 'pl-14'}>{g.activity.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
        <div className="flex justify-end">
          <Button variant="outline" size="sm" onClick={runPreview} disabled={!canPreview || loading}>
            {loading && <LoaderCircle className="h-4 w-4 animate-spin" />}
            {t('CostControl_allocate_preview')}
          </Button>
        </div>
        {error && <p className="text-sm text-destructive">{error}</p>}
        {preview && (
          <div className="max-h-[40vh] overflow-y-auto rounded-md border">
            <Table>
              <TableBody>
                {preview.map((l) => (
                  <TableRow key={l.nodeId}>
                    <TableCell>
                      {l.path && <div className="text-xs text-muted-foreground">{l.path}</div>}
                      <div>{l.nodeCode ? `${l.nodeCode} · ${l.nodeName}` : l.nodeName}</div>
                    </TableCell>
                    <TableCell className="text-right tabular-nums">{money(l.amount)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
        <div className="flex items-center justify-end gap-2 border-t pt-4">
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={loading}>{t('Common_cancel')}</Button>
          <Button onClick={apply} disabled={!preview || loading}>{t(applyKey)}</Button>
        </div>
      </DialogContent>
    </Dialog>
  )
}
export default AllocateDialog
